import { REGEX } from '@common/constants/global.constants'
import { CompanyType } from '@common/enums/company.enums'
import {
	DashboardTrainee,
	DashboardTraineeFormValues
} from '@common/types/person.types'
import TextInput from '@components/arthurBased/inputs/TextInput'
import CompanySelectInput from '@components/inputs/CompanySelectInput'
import PopIn from '@components/layout/PopIn'
import {
	Stack,
	styled
} from '@mui/material'
import { usePersonApi } from '@services/api/person.api'
import { useAuth } from '@services/contexts/AuthContext'
import { useSnackbar } from '@services/contexts/SnackbarContext'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import CertificationCreationPopIn from '../../../trainee/components/certifications/CertificationCreationPopIn'

type TraineeCreationPopInProps = {
	onClose: () => void
	onSuccess: () => void
	companyId?: string,
	companyLabel: string,
	setCompanyLabel: (label: string) => void
}

/**
 * Trainee creation pop-in
 * @param onClose
 * @param onConfirm
 * @param companyId
 * @param companyLabel
 * @constructor
 */
const TraineeCreationPopIn = (
	{
		onClose,
		onSuccess,
		companyId,
		companyLabel = '',
		setCompanyLabel
	}: TraineeCreationPopInProps
) => {
	const { user } = useAuth()
	const { formatMessage } = useIntl()
	const { createTrainee } = usePersonApi()
	const { addSnackbar, addErrorSnackbar } = useSnackbar()
	const [shouldCreateCertification, setShouldCreateCertification] = useState(false)
	const [traineeId, setTraineeId] = useState<string>()

	const {
		control,
		handleSubmit,
		watch
	} = useForm<DashboardTraineeFormValues>({
		defaultValues: {
			firstname: '',
			lastname: '',
			email: '',
			company: user.company?.type === CompanyType.END_USER ? user.company.id : companyId
		},
		mode: 'onBlur'
	})

	const watchCompany = watch('company')

	const { mutate: createTraineeMutate } = useMutation<DashboardTrainee, Error, DashboardTraineeFormValues>({
		mutationKey: ['createTrainee'],
		mutationFn: createTrainee,
		onSuccess: (data) => {
			if (shouldCreateCertification) {
				setTraineeId(data.id)
			} else {
				onClose()
			}

			onSuccess()
			addSnackbar(formatMessage({ id: 'dashboard.trainees.actions.addTrainee.success' }))
		},
		onError: (error) => addErrorSnackbar(formatMessage({ id: error.message }))
	})

	const onSubmit = (values: DashboardTraineeFormValues) => createTraineeMutate(values)

	return (
		<PopIn
			buttons={[
				{
					label: formatMessage({ id: 'dashboard.trainees.actions.addTrainee.buttons.create' }),
					onClick: () => {
						handleSubmit(onSubmit)()
						setShouldCreateCertification(false)
					}
				},
				{
					label: formatMessage({ id: 'dashboard.trainees.actions.addTrainee.buttons.createWithCertification' }),
					onClick: () => {
						handleSubmit(onSubmit)()
						setShouldCreateCertification(true)
					}
				}
			]}
			onClose={onClose}
			subtitle={formatMessage({ id: 'dashboard.trainees.actions.addTrainee.subtitle' })}
			title={formatMessage({ id: 'dashboard.trainees.actions.addTrainee.title' })}
			isOpen
		>
			<Form>
				<TextInput
					control={control}
					name="firstname"
					label={formatMessage({ id: 'global.fields.firstname' })}
					placeholder="Firstname"
					rules={{
						maxLength: 255
					}}
					isRequired
				/>
				<TextInput
					control={control}
					name="lastname"
					label={formatMessage({ id: 'global.fields.lastname' })}
					placeholder="Lastname"
					rules={{
						maxLength: 255
					}}
					isRequired
				/>
				<TextInput
					control={control}
					name="email"
					label={formatMessage({ id: 'dashboard.trainees.actions.addTrainee.fields.email.label' })}
					placeholder="Email"
					rules={{
						maxLength: 255,
						pattern: REGEX.EMAIL
					}}
				/>
				{user.company?.type !== CompanyType.END_USER && (
					<CompanySelectInput
						control={control}
						name="company"
						label={formatMessage({ id: 'dashboard.trainees.actions.addTrainee.fields.company.placeholder' })}
						companyLabel={watchCompany ? companyLabel : ''}
						setCompanyLabel={setCompanyLabel}
						isRequired
						isDisabled={!!companyId}
					/>
				)}
			</Form>
			{traineeId && (
				<CertificationCreationPopIn
					onClose={onClose}
					onSuccess={() => {
						onClose()
						onSuccess()
					}}
					traineeId={traineeId}
					isOpen
				/>
			)}
		</PopIn>
	)
}

const Form = styled(Stack)({
	rowGap: '16px'
})

export default TraineeCreationPopIn
